import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Area,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { CHART_CHROME } from '@/lib/chart';
import { useFilters } from '@/lib/filters';
import { useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/*
 * «Lønnsomhet» — written to be READ, not decoded.
 *
 * The owner's complaint about the previous version was exact: it showed which
 * numbers exist, not what is happening to his company. So this page is a
 * briefing, in the order a human actually asks:
 *   1. Går det bra?            — one sentence, one colour
 *   2. Hvor er vi på reisen?   — one chart, with the break-even line on it
 *   3. Hva avgjør det?         — the bathtub: in vs out, where the level settles
 *   4. Hva skjer hvis…?        — scenarios he can press
 *   5. Gjør dette nå           — three actions with kroner on them
 * Everything technical (benchmark sources, month table, method) is folded away.
 *
 * Defaults come from the last COMPLETE month: the current one is always half
 * done, which makes every rate look like a collapse.
 */

const LS_KEY = 'nono-profitability-assumptions-v2';
const DEFAULTS = { fixedMonthly: 220000, capacity: 750, openedAt: '' as string };
type Assumptions = typeof DEFAULTS;

function loadAssumptions(): Assumptions {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Assumptions>) };
  } catch {
    /* ignore */
  }
  return { ...DEFAULTS };
}

const MESSAGES = {
  nb: {
    title: 'Lønnsomhet',
    sub: 'Hvordan ligger vi an — i klartekst.',
    // verdict
    vNoReach: 'Nei — ikke med dagens tempo.',
    vOnWay: 'På vei, men ikke framme.',
    vProfit: 'Ja. Dere tjener penger nå.',
    vSubNoReach: (steady: string, be: string) =>
      `Fortsetter det som nå, stopper medlemstallet på rundt ${steady}. Dere trenger ${be} for å gå i null. Den forskjellen kommer ikke av seg selv — den må hentes.`,
    vSubOnWay: (months: string, be: string) =>
      `Holder dere dagens tempo, passerer dere ${be} medlemmer om cirka ${months} måneder. Da går driften i null.`,
    vSubProfit: (res: string) => `Driften går med ${res} i overskudd i måneden.`,
    ageLine: (months: number, members: string, opened: string) =>
      `Dere åpnet ${opened} — det er ${months} måneder siden — og har ${members} betalende medlemmer. Bransjen regner 100–150 medlemmer i løpet av det FØRSTE ÅRET som et godt resultat for et uavhengig senter, og 18–36 måneder som normal tid til break-even. Dere er langt foran den kurven.`,
    // journey
    journeyTitle: 'Reisen så langt',
    journeyNote:
      'Antall medlemmer ved slutten av hver måned, og helt til høyre: i dag. Den stiplede streken er der driften går i null. Medlemmer på en fri måned teller med — de er medlemmer, de betaler bare ikke akkurat den måneden.',
    journeyPre: (n: number, first: string) =>
      `De ${n} første månedene fra ${first} er utelatt: da var det nesten ingen medlemmer, og grafen startet der driften faktisk kom i gang.`,
    legendActual: 'Medlemmer',
    legendBreakeven: 'Break-even',
    // bathtub
    tubTitle: 'Det som avgjør alt',
    tubNote:
      'Tenk på det som et badekar. Nye medlemmer renner inn, folk som slutter renner ut. Nivået legger seg der de to møtes — uansett hvor mye dere markedsfører.',
    tubIn: 'Nye i perioden',
    tubOut: 'Sluttet i perioden',
    tubNet: 'Netto',
    tubLands: 'Nivået legger seg på',
    tubPeriod: (from: string, to: string, days: number, s: string, e: string) =>
      `${from} til ${to} (${days} dager): ${s} medlemmer ved start, ${e} ved slutt. Regnestykket går opp: start + nye + tilbakevendte − sluttet = slutt. Perioden styres av datovelgeren øverst.`,
    tubReturned: 'Kom tilbake',
    notReconciled:
      'Regnskapet går ikke opp: start + nye + tilbakevendte − sluttet er ikke lik antallet ved slutt. Ikke stol på tallene i denne perioden – si fra.',
    churnRate: 'Frafall',
    churnMath: (n: number, base: string, pct: string, days: number, monthly: string) =>
      `Frafallet er ${n} som sluttet i perioden, delt på et snitt på ${base} medlemmer = ${pct} % på ${days} dager, som tilsvarer ${monthly} % i måneden.`,
    churnListTitle: (n: number) => `Se hvem de ${n} er`,
    churnListNote:
      'Nøyaktig de samme personene som telles i «Sluttet i perioden» over. Har betalt hos oss og har ikke lenger et aktivt medlemskap i Momence. Vil du se flere, utvid perioden i datovelgeren øverst.',
    chName: 'Navn',
    chJoined: 'Ble medlem',
    chLast: 'Siste betaling',
    chLeft: 'Regnet som sluttet',
    chMonths: 'Måneder',
    chPaid: 'Betalt totalt',
    notReal: 'ikke ekte',
    notRealHint:
      'Marker som ikke et ekte medlem (ansatt, familie, test). Personen fjernes fra alle tall.',
    excludedTitle: (n: number) => `Merket som ikke ekte medlem (${n}) — klikk for å angre`,
    undoExclude: 'Angre: tell denne med igjen',
    // scenarios
    scenTitle: 'Hva skjer hvis …',
    scenNote: 'Trykk på et alternativ, eller juster selv. Alt regnes ut fra deres egne tall.',
    scenNow: 'Som i valgt periode',
    scenShort: (days: number) =>
      `NB: perioden er bare ${days} dager. Frafall regnet om til månedstakt fra så kort tid svinger mye — velg 30 dager eller mer for et tall å styre etter.`,
    scenAds: 'Skru på annonsene igjen',
    scenChurn: 'Halver frafallet',
    scenBoth: 'Begge deler',
    scenJoins: 'Nye medlemmer i måneden',
    scenChurnLabel: 'Frafall i måneden',
    scenResult: 'Da lander dere på',
    scenWhen: 'Dekker faste utgifter',
    scenWhenSub: 'Måneden driften bærer seg selv. Tidligere tap er ikke regnet inn.',
    cappedNote: (raw: string, cap: string) =>
      `medlemmer – taket. Regnestykket peker mot ${raw}, men senteret rommer ${cap}, så dere fyller opp.`,
    accumTitle: 'Samlet underskudd siden åpning',
    mrr: 'Inntekter i måneden',
    fixed: 'Faste kostnader',
    result: 'Månedlig resultat',
    resultSub: 'denne måneden',
    accumSub: 'hele måneder siden åpning',
    accumNote:
      'Summen av alle hele måneder: inntekter minus faste utgifter. Dette er det oppstarten har kostet så langt, og det hentes ikke inn den måneden driften går i null – det kommer etterpå.',
    scenNever: 'nås ikke',
    scenMonths: (n: number) => `om ~${n} måneder`,
    scenAlready: 'allerede nådd',
    // strengths / blockers
    goodTitle: 'Dette er sterkere enn bransjen',
    badTitle: 'Dette er det som stopper dere',
    // actions
    actTitle: 'Gjør dette nå',
    // details
    detailsTitle: 'Detaljer og kilder',
    thMonth: 'Måned',
    thPaying: 'Medlemmer',
    thJoined: 'Nye',
    thChurned: 'Sluttet',
    thMrr: 'Omsetning',
    thSpend: 'Annonser',
    thCac: 'Per medlem',
    benchTitle: 'Bransjefasit',
    metric: 'Tall',
    ours: 'Vårt',
    industry: 'Bransjen',
    assumptionsTitle: 'Dine egne tall',
    fixedLabel: 'Faste kostnader per måned (kr)',
    capacityLabel: 'Kapasitet (maks medlemmer)',
    openedLabel: 'Åpningsdato',
    openedHint: (derived: string, first: string) =>
      `Vi måler ${derived} — dagen ekte handel startet og ikke stoppet. Alt før er lagt bort som testbetalinger (aller første i systemet: ${first}). Sett den ekte dagen her om du vil.`,
    assumptionsNote: 'Alt annet på siden er målt fra salgene og Stripe. Disse to kjenner bare du.',
    members: 'medlemmer',
    perMonth: 'i måneden',
    today: 'i dag',
  },
  en: {
    title: 'Profitability',
    sub: 'How we are doing — in plain words.',
    vNoReach: 'No — not at the current pace.',
    vOnWay: 'On the way, but not there.',
    vProfit: 'Yes. You are making money.',
    vSubNoReach: (steady: string, be: string) =>
      `If this continues, membership settles at around ${steady}. You need ${be} to break even. That gap will not close by itself.`,
    vSubOnWay: (months: string, be: string) =>
      `At the current pace you pass ${be} members in about ${months} months. That is where the business breaks even.`,
    vSubProfit: (res: string) => `Operations run at ${res} profit per month.`,
    ageLine: (months: number, members: string, opened: string) =>
      `You opened ${opened} — ${months} months ago — with ${members} paying members today. The industry counts 100–150 members in the FIRST YEAR as a good result for an independent studio, and 18–36 months as normal time to break even. You are well ahead of that curve.`,
    journeyTitle: 'The journey so far',
    journeyNote:
      'Members at the end of each month, and on the far right: today. The dashed line is where the business breaks even. Members on a free month are included — they are members, they just are not paying that particular month.',
    journeyPre: (n: number, first: string) =>
      `The first ${n} months from ${first} are left out: there were almost no members then, and the chart starts where operations actually began.`,
    legendActual: 'Members',
    legendBreakeven: 'Break-even',
    tubTitle: 'What decides everything',
    tubNote:
      'Think of it as a bathtub. New members flow in, people who quit flow out. The level settles where the two meet — no matter how much you market.',
    tubIn: 'Joined in period',
    tubOut: 'Left in period',
    tubNet: 'Net',
    tubLands: 'The level settles at',
    tubPeriod: (from: string, to: string, days: number, s: string, e: string) =>
      `${from} to ${to} (${days} days): ${s} members at the start, ${e} at the end. The account balances: start + joined + returned − left = end. The period follows the date picker at the top.`,
    tubReturned: 'Came back',
    notReconciled:
      'The account does not balance: start + joined + returned − left does not equal the end count. Do not trust this period – flag it.',
    churnRate: 'Churn',
    churnMath: (n: number, base: string, pct: string, days: number, monthly: string) =>
      `Churn is ${n} people who left during the period over an average base of ${base} members = ${pct} % across ${days} days, which is ${monthly} % per month.`,
    churnListTitle: (n: number) => `See who those ${n} are`,
    churnListNote:
      'Exactly the same people counted in "Left in period" above. They have paid us and no longer hold an active membership in Momence. To see more, widen the period in the date picker at the top.',
    chName: 'Name',
    chJoined: 'Joined',
    chLast: 'Last payment',
    chLeft: 'Counted as left',
    chMonths: 'Months',
    chPaid: 'Paid in total',
    notReal: 'not real',
    notRealHint:
      'Mark as not a real member (staff, family, test). They are removed from every number.',
    excludedTitle: (n: number) => `Marked as not a real member (${n}) — click to undo`,
    undoExclude: 'Undo: count this person again',
    scenTitle: 'What happens if …',
    scenNote:
      'Press an option, or adjust it yourself. Everything is computed from your own numbers.',
    scenNow: 'As selected period',
    scenShort: (days: number) =>
      `Note: the period is only ${days} days. A monthly churn rate extrapolated from that swings wildly — pick 30 days or more for a number to steer by.`,
    scenAds: 'Turn the ads back on',
    scenChurn: 'Halve the churn',
    scenBoth: 'Both',
    scenJoins: 'New members per month',
    scenChurnLabel: 'Churn per month',
    scenResult: 'You land at',
    scenWhen: 'Dekker faste utgifter',
    scenWhenSub: 'Måneden driften bærer seg selv. Tidligere tap er ikke regnet inn.',
    cappedNote: (raw: string, cap: string) =>
      `medlemmer – taket. Regnestykket peker mot ${raw}, men senteret rommer ${cap}, så dere fyller opp.`,
    accumTitle: 'Samlet underskudd siden åpning',
    mrr: 'Inntekter i måneden',
    fixed: 'Faste kostnader',
    result: 'Månedlig resultat',
    resultSub: 'denne måneden',
    accumSub: 'hele måneder siden åpning',
    accumNote:
      'Summen av alle hele måneder: inntekter minus faste utgifter. Dette er det oppstarten har kostet så langt, og det hentes ikke inn den måneden driften går i null – det kommer etterpå.',
    scenNever: 'not reached',
    scenMonths: (n: number) => `in ~${n} months`,
    scenAlready: 'already reached',
    goodTitle: 'Stronger than the industry',
    badTitle: 'What is stopping you',
    actTitle: 'Do this now',
    detailsTitle: 'Details and sources',
    thMonth: 'Month',
    thPaying: 'Members',
    thJoined: 'Joined',
    thChurned: 'Left',
    thMrr: 'Revenue',
    thSpend: 'Ads',
    thCac: 'Per member',
    benchTitle: 'Industry benchmarks',
    metric: 'Metric',
    ours: 'Ours',
    industry: 'Industry',
    assumptionsTitle: 'Your own numbers',
    fixedLabel: 'Fixed costs per month (NOK)',
    capacityLabel: 'Capacity (max members)',
    openedLabel: 'Opening date',
    openedHint: (derived: string, first: string) =>
      `We measure ${derived} — the day real trade started and did not stop. Everything before is set aside as test payments (very first in the system: ${first}). Set the real day here if you want.`,
    assumptionsNote: 'Everything else is measured from sales and Stripe. Only you know these two.',
    members: 'members',
    perMonth: 'per month',
    today: 'today',
  },
};

const nf = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const kr = (n: number) => `${nf.format(Math.round(n))} kr`;

type Tone = 'good' | 'warn' | 'bad';
const TONE_BORDER: Record<Tone, string> = {
  good: 'border-l-4 border-l-good-fg',
  warn: 'border-l-4 border-l-warn-fg',
  bad: 'border-l-4 border-l-bad-fg',
};
const TONE_TEXT: Record<Tone, string> = {
  good: 'text-good-fg',
  warn: 'text-warn-fg',
  bad: 'text-bad-fg',
};

/**
 * Months until members reach `target`, or null if they never do. The room has
 * a ceiling: a projection that sails past capacity is arithmetic, not a plan,
 * so growth is clamped there every step.
 */
function monthsToTarget(
  start: number,
  joins: number,
  churnPct: number,
  target: number,
  capacity: number,
): number | null {
  if (start >= target) return 0;
  if (target > capacity) return null; // the room cannot hold break-even
  const c = churnPct / 100;
  const settles = c > 0 ? joins / c : Number.POSITIVE_INFINITY;
  if (Math.min(settles, capacity) < target) return null;
  let m = start;
  for (let i = 1; i <= 60; i += 1) {
    m = Math.min(capacity, m * (1 - c) + joins);
    if (m >= target) return i;
  }
  return null;
}

function Figure({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: string;
  tone?: Tone;
  sub?: string | undefined;
}) {
  return (
    <div className="min-w-0">
      <div className="text-label text-fg-muted">{label}</div>
      <div
        className={cn(
          'mt-0.5 truncate text-xl font-bold tabular',
          tone ? TONE_TEXT[tone] : 'text-fg',
        )}
      >
        {value}
      </div>
      {sub ? <div className="mt-0.5 text-micro text-fg-subtle">{sub}</div> : null}
    </div>
  );
}

function Panel({
  title,
  note,
  children,
  className,
}: {
  title?: string;
  note?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn('rounded-lg border border-border bg-surface p-4 shadow-card sm:p-5', className)}
    >
      {title ? <h2 className="text-section font-semibold text-fg">{title}</h2> : null}
      {note ? (
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-fg-subtle">{note}</p>
      ) : null}
      <div className={title || note ? 'mt-4' : ''}>{children}</div>
    </section>
  );
}

export function ProfitabilityPage() {
  const m = useViewMessages(MESSAGES);
  const [a, setA] = useState<Assumptions>(loadAssumptions);
  const saveA = (next: Assumptions) => {
    setA(next);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  // the bathtub follows the period picker at the top, like every other page
  const { from: fFrom, to: fTo } = useFilters();
  const qc = useQueryClient();
  const exclude = useMutation({
    mutationFn: (v: { momenceId: string; name?: string; remove?: boolean }) => api.healthExclude(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['health-check'] }),
  });
  const q = useQuery({
    queryKey: ['health-check', fFrom, fTo],
    queryFn: () => api.healthCheck({ from: fFrom, to: fTo }),
    placeholderData: (prev) => prev,
  });
  const d = q.data;

  // the last COMPLETE month is the honest baseline
  const base = useMemo(() => {
    if (!d) return null;
    const complete = d.months.filter((x) => x.month !== d.currentMonth);
    const last = complete[complete.length - 1];
    const arpu = d.measured.arpu ?? 0;
    const active = d.measured.activeNow;
    const breakeven = arpu > 0 ? Math.ceil(a.fixedMonthly / arpu) : 0;
    // the ad-funded month with the most joins we have actually achieved
    const bestJoins = complete.reduce((mx, x) => Math.max(mx, x.joined), 0);
    return {
      last,
      arpu,
      active,
      breakeven,
      bestJoins,
      joins: last?.joined ?? 0,
      churn: last?.churnPct ?? d.measured.churn3mPct ?? 0,
      mrr: active * arpu,
    };
  }, [d, a.fixedMonthly]);

  const [joins, setJoins] = useState<number | null>(null);
  const [churn, setChurn] = useState<number | null>(null);

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !d || !base) return <ErrorState onRetry={() => void q.refetch()} />;

  const P = d.period;
  // Scenario defaults follow the SAME period as the bathtub above — two
  // different churn numbers on one screen is how you lose a reader.
  const periodJoinsPerMonth = Math.round(P.joined * (30.44 / P.days));
  const periodChurn = P.churnPctMonthly;
  const J = joins ?? periodJoinsPerMonth;
  const C = churn ?? periodChurn ?? base.churn;
  const shortPeriod = P.days < 28;
  const steadyRaw = C > 0 ? J / (C / 100) : null;
  // The equilibrium the maths wants, and the one the room allows.
  const steady = steadyRaw != null ? Math.min(steadyRaw, a.capacity) : null;
  const cappedByRoom = steadyRaw != null && steadyRaw > a.capacity;
  const monthsTo = monthsToTarget(base.active, J, C, base.breakeven, a.capacity);
  const monthlyResult = base.mrr - a.fixedMonthly;

  // baseline verdict uses the measured numbers, never the scenario sliders
  const verdictChurn = periodChurn ?? base.churn;
  const verdictJoins = periodJoinsPerMonth;
  const baseSteady =
    verdictChurn > 0 ? Math.min(verdictJoins / (verdictChurn / 100), a.capacity) : null;
  const baseMonths = monthsToTarget(
    base.active,
    verdictJoins,
    verdictChurn,
    base.breakeven,
    a.capacity,
  );
  const verdictTone: Tone = monthlyResult >= 0 ? 'good' : baseMonths != null ? 'warn' : 'bad';
  const verdictHead = monthlyResult >= 0 ? m.vProfit : baseMonths != null ? m.vOnWay : m.vNoReach;
  const verdictSub =
    monthlyResult >= 0
      ? m.vSubProfit(kr(monthlyResult))
      : baseMonths != null
        ? m.vSubOnWay(nf.format(baseMonths), nf.format(base.breakeven))
        : m.vSubNoReach(
            baseSteady != null ? nf.format(baseSteady) : '–',
            nf.format(base.breakeven),
          );

  // The chart must not lie at a glance. Two things made it misread:
  //   • the four near-empty months before the real start looked like a dead
  //     business (3 members in January, then quiet until May)
  //   • the current, half-finished month ends in a cliff that is pure artefact
  // So: complete months only, from where operations actually began.
  // The derived opening is the first payment we can see — which for a gym with
  // a presale lands before the doors opened. Only the owner knows the real day,
  // so his value wins when he sets one.
  const effectiveOpened = a.openedAt || d.openedAt;
  const monthsOpen = effectiveOpened
    ? Math.max(
        0,
        Math.round(
          (Date.now() - Date.parse(`${effectiveOpened}T12:00:00Z`)) / (30.44 * 86_400_000),
        ),
      )
    : d.monthsOpen;
  const openedLabel = effectiveOpened
    ? new Date(`${effectiveOpened}T12:00:00Z`).toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : (d.openedMonth ?? '–');
  // Members is now a STOCK measured identically at every month end, and the
  // final point IS today — so the chart runs all the way to now. A chart that
  // stopped at the last complete month answered a question nobody asked.
  const chartData = d.months.map((x, i) => ({
    month: i === d.months.length - 1 ? m.today : x.month.slice(2),
    members: x.payingMembers,
  }));

  const steadyPeriod =
    P.churnPctMonthly != null && P.churnPctMonthly > 0
      ? (P.joined * (30.44 / P.days)) / (P.churnPctMonthly / 100)
      : null;

  // What the ramp has cost so far — the owner asked whether break-even means
  // clawing this back. It does not, and now the page says both numbers.
  const accumulated = d.months
    .filter((x) => x.month !== d.currentMonth)
    .reduce((s, x) => s + (x.mrr - a.fixedMonthly), 0);

  const ltvCac =
    d.measured.ltv != null && d.measured.cacBlended ? d.measured.ltv / d.measured.cacBlended : null;
  const payback =
    d.measured.cacBlended != null && base.arpu > 0 ? d.measured.cacBlended / base.arpu : null;
  const r2 = d.retentionCurve.find((r) => r.month === 2);
  const cliff = r2?.pct != null ? 100 - r2.pct : null;
  const failedMoney = d.stripe.failed30d * base.arpu;

  const strengths: string[] = [];
  if (base.arpu >= 600)
    strengths.push(
      `Hvert medlem betaler ${kr(base.arpu)} i måneden. Norske sentre ligger på 560–620 kr, SATS på 607 kr. Dere tar mer enn markedet, og folk betaler.`,
    );
  if (d.measured.cacBlended != null && d.measured.cacBlended < 1500)
    strengths.push(
      `Det koster ${kr(d.measured.cacBlended)} å skaffe ett nytt medlem. Tilsvarende sentre betaler 3 800–6 600 kr. Dere er fire til åtte ganger billigere.`,
    );
  if (payback != null && payback < 3)
    strengths.push(
      `Pengene dere bruker på å skaffe et medlem er tilbake på ${nf1.format(payback)} måneder. Under seks måneder regnes som bra.`,
    );
  if (cliff != null && cliff < 47)
    strengths.push(
      `${nf.format(cliff)} % faller fra i løpet av de tre første månedene. Det høres mye ut, men hele bransjen ligger på 47–63 %. Dere holder bedre på folk enn snittet.`,
    );
  strengths.push(
    `Dere har ${nf.format(base.active)} medlemmer etter bare ${monthsOpen} måneders drift. 100–150 i løpet av det første året regnes som et godt resultat for et uavhengig senter — dere er der på en brøkdel av tiden.`,
  );

  const blockers: string[] = [];
  if (baseSteady != null && baseSteady < base.breakeven)
    blockers.push(
      `Med ${nf.format(base.joins)} nye og ${nf.format(base.churn)} % frafall i måneden stopper medlemstallet på ${nf.format(baseSteady)}. Break-even er ${nf.format(base.breakeven)}. Slik det står nå kommer dere aldri fram.`,
    );
  if (monthlyResult < 0)
    blockers.push(
      `Driften går ${kr(Math.abs(monthlyResult))} i minus hver måned: ${kr(base.mrr)} inn mot ${kr(a.fixedMonthly)} i faste kostnader.`,
    );
  const lastSpend = base.last?.adSpend ?? null;
  if (lastSpend != null && base.bestJoins > base.joins * 1.3)
    blockers.push(
      `Da dere brukte mest på annonser fikk dere ${nf.format(base.bestJoins)} nye på én måned. Sist hele måned ble det ${nf.format(base.joins)}. Vekstmotoren er skrudd ned, ikke ødelagt.`,
    );
  if (d.stripe.failed30d > 0)
    blockers.push(
      `${nf.format(d.stripe.failed30d)} korttrekk feilet siste måned — omtrent ${kr(failedMoney)}. Ingen av dem valgte å slutte; kortet gikk bare ikke gjennom.`,
    );

  const actions: { title: string; body: string; value: string }[] = [
    {
      title: 'Ring dem med feilet kort',
      body: `${nf.format(d.stripe.failed90d)} trekk har feilet de siste 90 dagene. Det er medlemmer som fortsatt vil være her.`,
      value: `~${kr(failedMoney)} ${m.perMonth}`,
    },
    {
      title: 'Skru annonsene opp igjen',
      body: `Pengene er tilbake på ${payback != null ? nf1.format(payback) : '–'} måneder. Hver krone dere holder igjen er vekst dere ikke får.`,
      value: `${nf.format(base.bestJoins)} nye på én måned da det sto på`,
    },
    {
      title: 'Få de nye i gang første måned',
      body: 'Antall besøk den første måneden er den sterkeste varsleren for hvem som blir. Fire besøk eller mer, og de blir stort sett værende.',
      value: 'Halvert frafall = break-even innen rekkevidde',
    },
  ];

  const setScenario = (jj: number, cc: number) => {
    setJoins(Math.round(jj));
    setChurn(Math.round(cc));
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold">{m.title}</h1>
        <p className="text-sm text-fg-subtle">{m.sub}</p>
      </div>

      {/* 1 — the verdict */}
      <Panel className={TONE_BORDER[verdictTone]}>
        <div className={cn('text-2xl font-bold sm:text-3xl', TONE_TEXT[verdictTone])}>
          {verdictHead}
        </div>
        <p className="mt-2 max-w-3xl text-base leading-relaxed text-fg">{verdictSub}</p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-fg-subtle">
          {m.ageLine(monthsOpen, nf.format(base.active), openedLabel)}
        </p>

        {/* the money, in four numbers, right where the reader already is */}
        <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4 md:grid-cols-4">
          <Figure
            label={m.mrr}
            value={kr(base.mrr)}
            sub={`${nf.format(base.active)} × ${kr(base.arpu)}`}
          />
          <Figure label={m.fixed} value={kr(a.fixedMonthly)} />
          <Figure
            label={m.result}
            value={`${monthlyResult >= 0 ? '+' : ''}${kr(monthlyResult)}`}
            tone={monthlyResult >= 0 ? 'good' : 'bad'}
            sub={m.resultSub}
          />
          <Figure
            label={m.accumTitle}
            value={`${accumulated >= 0 ? '+' : ''}${kr(accumulated)}`}
            tone={accumulated >= 0 ? 'good' : 'bad'}
            sub={m.accumSub}
          />
        </div>
        <p className="mt-2 max-w-3xl text-xs leading-relaxed text-fg-subtle">{m.accumNote}</p>
      </Panel>

      {/* 2 — the journey */}
      <Panel title={m.journeyTitle} note={m.journeyNote}>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: CHART_CHROME.axis }}
                tickLine={false}
                axisLine={false}
              />
              {/* the break-even line is the POINT of this chart — the axis must
                  reach it, otherwise the reference line renders off-screen */}
              <YAxis
                domain={[
                  0,
                  Math.ceil(
                    (Math.max(base.breakeven, ...chartData.map((c) => c.members)) * 1.1) / 25,
                  ) * 25,
                ]}
                tick={{ fontSize: 11, fill: CHART_CHROME.axis }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number) => [`${nf.format(v)} ${m.members}`, m.legendActual]}
              />
              {/* The axis has to reach break-even, which squashes the member
                  curve into the lower third — so the curve itself must be bold
                  and carry its own numbers, or the chart reads as empty. */}
              <Area
                type="monotone"
                dataKey="members"
                stroke="var(--accent-booking)"
                fill="var(--accent-booking)"
                fillOpacity={0.28}
                strokeWidth={0}
              />
              <Line
                type="monotone"
                dataKey="members"
                stroke="var(--accent-booking)"
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)' }}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="members"
                  position="top"
                  offset={10}
                  style={{ fontSize: 12, fontWeight: 600, fill: 'var(--fg)' }}
                />
              </Line>
              <ReferenceLine
                y={base.breakeven}
                stroke="var(--warn-fg)"
                strokeDasharray="5 4"
                label={{
                  value: `${m.legendBreakeven} ${nf.format(base.breakeven)}`,
                  position: 'insideTopRight',
                  fill: 'var(--warn-fg)',
                  fontSize: 11,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {/* 3 — the bathtub */}
      <Panel title={m.tubTitle} note={m.tubNote}>
        <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
          <div>
            <div className="text-label text-fg-muted">{m.tubIn}</div>
            <div className="mt-0.5 text-3xl font-bold tabular text-good-fg">
              +{nf.format(P.joined)}
            </div>
          </div>
          <div>
            <div className="text-label text-fg-muted">{m.tubOut}</div>
            <div className="mt-0.5 text-3xl font-bold tabular text-bad-fg">
              −{nf.format(P.churned)}
            </div>
          </div>
          {P.returned > 0 ? (
            <div>
              <div className="text-label text-fg-muted">{m.tubReturned}</div>
              <div className="mt-0.5 text-3xl font-bold tabular text-good-fg">
                +{nf.format(P.returned)}
              </div>
            </div>
          ) : null}
          <div>
            <div className="text-label text-fg-muted">{m.tubNet}</div>
            <div className="mt-0.5 text-3xl font-bold tabular">
              {P.joined + P.returned - P.churned >= 0 ? '+' : ''}
              {nf.format(P.joined + P.returned - P.churned)}
            </div>
          </div>
          <div>
            <div className="text-label text-fg-muted">{m.churnRate}</div>
            <div
              className={cn(
                'mt-0.5 text-3xl font-bold tabular',
                P.churnPctMonthly == null
                  ? 'text-fg'
                  : P.churnPctMonthly <= 3
                    ? 'text-good-fg'
                    : P.churnPctMonthly <= 5
                      ? 'text-warn-fg'
                      : 'text-bad-fg',
              )}
            >
              {P.churnPctMonthly != null ? `${nf1.format(P.churnPctMonthly)} %` : '–'}
            </div>
            <div className="text-micro text-fg-subtle">{m.perMonth}</div>
          </div>
          <div className="ml-auto rounded-md bg-surface-2 px-4 py-3">
            <div className="text-label text-fg-muted">{m.tubLands}</div>
            <div
              className={cn(
                'mt-0.5 text-3xl font-bold tabular',
                steadyPeriod != null && steadyPeriod >= base.breakeven
                  ? 'text-good-fg'
                  : 'text-bad-fg',
              )}
            >
              {steadyPeriod != null ? nf.format(steadyPeriod) : '–'}
            </div>
            <div className="text-micro text-fg-subtle">
              {m.legendBreakeven}: {nf.format(base.breakeven)}
            </div>
          </div>
        </div>
        {!P.reconciles ? (
          <p className="mt-3 rounded-md bg-bad-bg px-3 py-2 text-xs font-semibold text-bad-fg">
            {m.notReconciled}
          </p>
        ) : null}
        <p className="mt-3 text-xs text-fg-subtle">
          {m.tubPeriod(P.from, P.to, P.days, nf.format(P.membersStart), nf.format(P.membersEnd))}
          {P.churned > 0 && P.avgMembers > 0
            ? ` ${m.churnMath(
                P.churned,
                nf.format(P.avgMembers),
                nf1.format(P.churnPct ?? 0),
                P.days,
                nf1.format(P.churnPctMonthly ?? 0),
              )}`
            : ''}
        </p>

        {d.excluded.length > 0 ? (
          <div className="mt-3 rounded-md border border-border bg-surface-2 px-3 py-2">
            <div className="text-label text-fg-muted">{m.excludedTitle(d.excluded.length)}</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {d.excluded.map((e) => (
                <button
                  key={e.momenceId}
                  type="button"
                  title={m.undoExclude}
                  onClick={() => exclude.mutate({ momenceId: e.momenceId, remove: true })}
                  className="rounded-full border border-border px-2 py-0.5 text-micro text-fg-subtle hover:border-brand hover:text-brand"
                >
                  {e.name} ✕
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {d.churnList.length > 0 ? (
          <details className="mt-3 rounded-md border border-border bg-surface-2 p-3">
            <summary className="cursor-pointer text-sm font-semibold text-fg">
              {m.churnListTitle(d.churnList.length)}
            </summary>
            <p className="mt-1 text-xs leading-relaxed text-fg-subtle">{m.churnListNote}</p>
            <div className="mt-2 max-h-80 overflow-y-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="text-label text-fg-muted">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">{m.chName}</th>
                    <th className="px-2 py-1.5 text-left font-semibold">{m.chJoined}</th>
                    <th className="px-2 py-1.5 text-left font-semibold">{m.chLast}</th>
                    <th className="px-2 py-1.5 text-left font-semibold">{m.chLeft}</th>
                    <th className="px-2 py-1.5 text-right font-semibold">{m.chMonths}</th>
                    <th className="px-2 py-1.5 text-right font-semibold">{m.chPaid}</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {d.churnList.map((c) => (
                    <tr key={`${c.name}-${c.lastPaidAt}`}>
                      <td className="px-2 py-1.5 font-medium">{c.name}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-fg-muted">{c.joinedAt}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-fg-muted">
                        {c.lastPaidAt}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-fg-muted">
                        {c.leftAt ?? '–'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular">{c.monthsStayed}</td>
                      <td className="px-2 py-1.5 text-right tabular">{kr(c.totalPaid)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          type="button"
                          title={m.notRealHint}
                          disabled={exclude.isPending}
                          onClick={() => exclude.mutate({ momenceId: c.momenceId, name: c.name })}
                          className="rounded px-1.5 py-0.5 text-micro text-fg-subtle transition-fast hover:bg-bad-bg hover:text-bad-fg disabled:opacity-40"
                        >
                          {m.notReal}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ) : null}
      </Panel>

      {/* 4 — scenarios */}
      <Panel
        title={m.scenTitle}
        note={shortPeriod ? `${m.scenNote} ${m.scenShort(P.days)}` : m.scenNote}
      >
        <div className="flex flex-wrap gap-2">
          {[
            { label: m.scenNow, j: periodJoinsPerMonth, c: verdictChurn },
            { label: m.scenAds, j: base.bestJoins, c: verdictChurn },
            { label: m.scenChurn, j: periodJoinsPerMonth, c: verdictChurn / 2 },
            { label: m.scenBoth, j: base.bestJoins, c: verdictChurn / 2 },
          ].map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => setScenario(s.j, s.c)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs font-semibold transition-fast',
                Math.round(J) === Math.round(s.j) && Math.round(C) === Math.round(s.c)
                  ? 'border-brand bg-brand-tint text-brand'
                  : 'border-border hover:bg-surface-2',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1">
            <span className="text-label text-fg-muted">{m.scenJoins}</span>
            <input
              type="range"
              min={0}
              max={Math.max(80, base.bestJoins + 20)}
              value={J}
              onChange={(e) => setJoins(Number(e.target.value))}
              className="accent-[var(--brand)]"
            />
            <span className="text-xl font-bold tabular">{nf.format(J)}</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-label text-fg-muted">{m.scenChurnLabel}</span>
            <input
              type="range"
              min={1}
              max={50}
              value={C}
              onChange={(e) => setChurn(Number(e.target.value))}
              className="accent-[var(--brand)]"
            />
            <span className="text-xl font-bold tabular">{nf.format(C)} %</span>
          </label>
          <div>
            <div className="text-label text-fg-muted">{m.scenResult}</div>
            <div
              className={cn(
                'mt-1 text-3xl font-bold tabular',
                steady != null && steady >= base.breakeven ? 'text-good-fg' : 'text-bad-fg',
              )}
            >
              {steady != null ? nf.format(steady) : '–'}
            </div>
            <div className="text-micro text-fg-subtle">
              {cappedByRoom && steadyRaw != null
                ? m.cappedNote(nf.format(steadyRaw), nf.format(a.capacity))
                : m.members}
            </div>
          </div>
          <div>
            <div className="text-label text-fg-muted">{m.scenWhen}</div>
            <div className="text-micro leading-snug text-fg-subtle">{m.scenWhenSub}</div>
            <div
              className={cn(
                'mt-1 text-3xl font-bold',
                monthsTo != null ? 'text-good-fg' : 'text-bad-fg',
              )}
            >
              {monthsTo == null
                ? m.scenNever
                : monthsTo === 0
                  ? m.scenAlready
                  : m.scenMonths(monthsTo)}
            </div>
          </div>
        </div>
      </Panel>

      {/* 5 — strengths and blockers */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={m.goodTitle} className="border-l-4 border-l-good-fg">
          <ul className="flex flex-col gap-2.5">
            {strengths.map((s) => (
              <li key={s} className="text-sm leading-relaxed text-fg">
                {s}
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title={m.badTitle} className="border-l-4 border-l-bad-fg">
          <ul className="flex flex-col gap-2.5">
            {blockers.map((s) => (
              <li key={s} className="text-sm leading-relaxed text-fg">
                {s}
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {/* 6 — actions */}
      <Panel title={m.actTitle}>
        <ol className="flex flex-col gap-3">
          {actions.map((act, i) => (
            <li key={act.title} className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-tint text-xs font-bold text-brand">
                {i + 1}
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-fg">{act.title}</div>
                <div className="text-sm leading-relaxed text-fg-subtle">{act.body}</div>
                <div className="mt-0.5 text-sm font-semibold text-good-fg">{act.value}</div>
              </div>
            </li>
          ))}
        </ol>
      </Panel>

      {/* 7 — everything technical, folded away */}
      <details className="rounded-lg border border-border bg-surface p-4 shadow-card">
        <summary className="cursor-pointer text-section font-semibold text-fg">
          {m.detailsTitle}
        </summary>

        <h3 className="mt-4 text-label font-semibold text-fg-muted">{m.benchTitle}</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-border text-label text-fg-muted">
              <tr>
                <th className="px-2 py-2 text-left font-semibold">{m.metric}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.ours}</th>
                <th className="px-2 py-2 text-left font-semibold">{m.industry}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                { k: 'arpu', l: 'Pris per medlem', v: base.arpu != null ? kr(base.arpu) : '–' },
                {
                  k: 'cac',
                  l: 'Kost per nytt medlem',
                  v: d.measured.cacBlended != null ? kr(d.measured.cacBlended) : '–',
                },
                {
                  k: 'ltvCac',
                  l: 'Verdi mot kostnad',
                  v: ltvCac != null ? `${nf1.format(ltvCac)}:1` : '–',
                },
                {
                  k: 'paybackMonths',
                  l: 'Tilbakebetaling',
                  v: payback != null ? `${nf1.format(payback)} mnd` : '–',
                },
                {
                  k: 'cliff90d',
                  l: 'Frafall første 3 mnd',
                  v: cliff != null ? `${nf.format(cliff)} %` : '–',
                },
                { k: 'churnMonthly', l: 'Frafall per måned', v: `${nf.format(base.churn)} %` },
                {
                  k: 'failedChargePct',
                  l: 'Feilede korttrekk',
                  v: d.stripe.failedPct90d != null ? `${nf1.format(d.stripe.failedPct90d)} %` : '–',
                },
                { k: 'monthsToBreakeven', l: 'Måneder i drift', v: `${nf.format(monthsOpen)} mnd` },
              ].map((row) => (
                <tr key={row.k} className="align-top">
                  <td className="px-2 py-2 font-medium">{row.l}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-right font-semibold tabular">
                    {row.v}
                  </td>
                  <td className="px-2 py-2 text-xs leading-relaxed text-fg-subtle">
                    {d.benchmarks[row.k]?.industry ?? '–'}
                    {d.benchmarks[row.k] ? (
                      <span className="block text-micro opacity-70">
                        {d.benchmarks[row.k]?.source}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-6 text-label font-semibold text-fg-muted">{m.journeyTitle}</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="border-b border-border text-label text-fg-muted">
              <tr>
                <th className="px-2 py-2 text-left font-semibold">{m.thMonth}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.thPaying}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.thJoined}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.thChurned}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.thMrr}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.thSpend}</th>
                <th className="px-2 py-2 text-right font-semibold">{m.thCac}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {d.months.map((r) => (
                <tr key={r.month} className={cn(r.month === d.currentMonth && 'opacity-50')}>
                  <td className="px-2 py-1.5 font-medium">{r.month}</td>
                  <td className="px-2 py-1.5 text-right tabular">{nf.format(r.payingMembers)}</td>
                  <td className="px-2 py-1.5 text-right tabular text-good-fg">
                    +{nf.format(r.joined)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular text-bad-fg">
                    {r.churned > 0 ? `−${nf.format(r.churned)}` : '–'}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular">{kr(r.mrr)}</td>
                  <td className="px-2 py-1.5 text-right tabular text-fg-subtle">
                    {r.adSpend != null ? kr(r.adSpend) : '–'}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular text-fg-subtle">
                    {r.cacPerMember != null ? kr(r.cacPerMember) : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-6 text-label font-semibold text-fg-muted">{m.assumptionsTitle}</h3>
        <p className="mt-1 text-xs text-fg-subtle">{m.assumptionsNote}</p>
        <div className="mt-2 flex flex-wrap gap-4">
          <label className="flex flex-col gap-1 text-label text-fg-muted">
            {m.fixedLabel}
            <input
              type="number"
              value={a.fixedMonthly}
              onChange={(e) => saveA({ ...a, fixedMonthly: Number(e.target.value) || 0 })}
              className="w-44 rounded-md border border-border bg-surface px-3 py-1.5 text-sm tabular"
            />
          </label>
          <label className="flex flex-col gap-1 text-label text-fg-muted">
            {m.openedLabel}
            <input
              type="date"
              value={a.openedAt || d.openedAt || ''}
              max={d.today}
              onChange={(e) => saveA({ ...a, openedAt: e.target.value })}
              className="w-44 rounded-md border border-border bg-surface px-3 py-1.5 text-sm tabular"
            />
            <span className="max-w-44 text-micro leading-snug text-fg-subtle">
              {m.openedHint(d.openedAt ?? '–', d.firstPaymentEver ?? '–')}
            </span>
          </label>
          <label className="flex flex-col gap-1 text-label text-fg-muted">
            {m.capacityLabel}
            <input
              type="number"
              value={a.capacity}
              onChange={(e) => saveA({ ...a, capacity: Number(e.target.value) || 0 })}
              className="w-44 rounded-md border border-border bg-surface px-3 py-1.5 text-sm tabular"
            />
          </label>
        </div>
      </details>
    </div>
  );
}
